import {displayShortDate} from "@ui-kit/utils/static/date-format-utils";
import {displayString} from "@ui-kit/utils/static/string-format-util";
import {
  Date,
  DisplayFunction,
  DisplayOrder,
  Hidden,
  String,
  TableDisplayedDefault
} from "@ui-webkit/components/crud-item/crud-item-decorator";
import {
  alphanumericPattern,
  codeListPattern,
  datePattern,
  emailPattern,
  numberPattern,
  UploadDataModel,
  uuidPattern
} from "@ui-webkit/components/csv-import/csv-import.model";

export interface LeaseUpload {
  userId: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  retailerId: number;
  retailerCompanyName: string;
  retailerIndustries: string;
  leaseParentUuid: string;
  leaseStartDate: string;
  leaseEndDate: string;
  leaseSpaceSize: number;
  errorMessages: string[];
}

export interface CreateBatchLease {
  leaseParentUuid: string
  subLeases: LeaseUpload[];
  errorMessage: string;
}

export interface Lease {
  uuid: string;
  parentUuid: string | null;
  retailerId: number;
  retailerCompanyName: string;
  districtUuid?: string;
  leaseStartDate: string;
  leaseEndDate: string;
  totalSize: number;
  leaseStatus: string;
}

export const leaseUploadModel: UploadDataModel[] = [
  {
    property: 'userId',
    label: 'User ID',
    required: false,
    validators: [numberPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: false
  },
  {
    property: 'userEmail',
    label: 'Email',
    required: true,
    validators: [emailPattern],
    validationMessage: 'Requires a valid email address',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'userFirstName',
    label: 'First Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid name.',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'userLastName',
    label: 'Last Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid name',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'retailerId',
    label: 'Retailer ID',
    required: false,
    validators: [numberPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: false
  },
  {
    property: 'retailerCompanyName',
    label: 'Company Name',
    required: true,
    validators: [],
    validationMessage: 'Requires a valid company name',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'retailerIndustries',
    label: 'Industries',
    required: true,
    validators: [codeListPattern],
    validationMessage: 'Requires a list of industry codes separated by commas',
    description: 'e.g. SKINCARE_COSMETICS,BATH_BODY',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'leaseParentUuid',
    label: 'Parent Lease Uuid',
    required: false,
    validators: [uuidPattern],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: false,
    computeValue: true
  },
  {
    property: 'leaseStartDate',
    label: 'Start Date',
    required: true,
    validators: [datePattern],
    validationMessage: 'Invalid format, requires: yyyy-MM-dd',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'leaseEndDate',
    label: 'End Date',
    required: true,
    validators: [datePattern],
    validationMessage: 'Invalid format, requires: yyyy-MM-dd',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'leaseSpaceSize',
    label: 'Space Size',
    required: true,
    validators: [numberPattern],
    validationMessage: 'Requires a number greater than 0',
    description: '',
    selectColumn: true,
    showInTable: true,
    computeValue: false
  },
  {
    property: 'errorMessages',
    label: 'Error',
    required: false,
    validators: [],
    validationMessage: '',
    description: '',
    selectColumn: false,
    showInTable: true,
    computeValue: false
  },
]

export interface Lease {
  uuid: string;
  parentUuid: string | null;
  retailerId: number;
  retailerCompanyName: string;
  districtUuid?: string;
  leaseStartDate: string;
  leaseEndDate: string;
  totalSize: number;
  leaseStatus: string;
}

export class LeaseSummary {
  @Hidden
  uuid?: string;

  @Hidden
  parentUuid?: string;

  @String('Retailer ID')
  @DisplayOrder(1)
  @TableDisplayedDefault
  retailerId?: number;

  @String('Company Name')
  @DisplayOrder(2)
  @TableDisplayedDefault
  retailerCompanyName?: number;

  @Hidden
  districtUuid?: string;

  @Date('Start Date', 'yyyy-MM-dd')
  @DisplayOrder(3)
  @TableDisplayedDefault
  @DisplayFunction(displayShortDate)
  leaseStartDate?: string;

  @Date('End Date', 'yyyy-MM-dd')
  @DisplayOrder(4)
  @TableDisplayedDefault
  @DisplayFunction(displayShortDate)
  leaseEndDate?: string;

  @String("Space (m²)")
  @DisplayOrder(5)
  @TableDisplayedDefault
  totalSize?: string;

  @String("Lease Status")
  @DisplayOrder(6)
  @TableDisplayedDefault
  @DisplayFunction(displayString)
  leaseStatus?: string;

  constructor(obj: any = {}) {
    obj = obj || {};
    this.uuid = obj.uuid;
    this.parentUuid = obj.parentUuid;
    this.districtUuid = obj.districtUuid;
    this.retailerId = obj.retailerId;
    this.retailerCompanyName = obj.retailerCompanyName;
    this.totalSize = obj.totalSize;
    this.leaseStatus = obj.leaseStatus;
    this.leaseStartDate = obj.leaseStartDate;
    this.leaseEndDate = obj.leaseEndDate;
  }
}
